import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[var(--border-ink)] py-3 text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--gold)] outline-none transition-colors";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1511285560929-80b456fea0bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Wedding"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[var(--ink)]/40" />
        <div className="absolute bottom-10 left-10 text-[var(--bg)]">
          <p className="font-serif text-3xl leading-tight max-w-sm">
            Every great celebration begins behind the scenes.
          </p>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-16 py-16 bg-[var(--bg)]">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-[var(--ink-3)] hover:text-[var(--ink)] mb-10"
          data-testid="admin-back-home"
        >
          <ArrowLeft size={16} /> Back to site
        </button>

        <Logo className="h-12 w-auto mb-8" />
        <h1 className="font-serif text-4xl tracking-tight">Studio Login</h1>
        <p className="text-[var(--ink-2)] mt-2 font-light">Manage inquiries & availability.</p>

        <form onSubmit={submit} className="mt-10 space-y-6 max-w-sm" data-testid="admin-login-form">
          <input
            className={inputCls}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-login-email"
          />
          <input
            className={inputCls}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-login-password"
          />
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-3 rounded-full bg-[var(--ink)] text-[var(--bg)] px-8 py-3.5 text-sm tracking-wide hover:bg-[var(--gold)] transition-colors disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
